import { createContext, type ReactNode, useContext, useMemo } from "react"

/**
 * Repeat scope — exposes the current row's item and index to descendant
 * elements via React context so `useResolvedElement` can include them
 * in the ResolveContext for `{$item: "field"}` and `{$index: true}`
 * expressions to resolve.
 *
 * Mirrors `vercel-labs/json-render`'s `repeatItem` / `repeatIndex`
 * fields on its prop-resolution context. Stays a context (not a prop)
 * so deeply-nested children inside a Repeat body don't have to thread
 * the scope through every layer of layout primitives.
 */
export interface RepeatScopeValue {
  item: unknown
  index: number
}

const RepeatScopeContext = createContext<RepeatScopeValue | null>(null)

export function RepeatScopeProvider({
  item,
  index,
  children,
}: {
  item: unknown
  index: number
  children: ReactNode
}) {
  // Memo the value so children don't re-render on every parent render
  // when item/index reference identity is stable.
  const value = useMemo<RepeatScopeValue>(() => ({ item, index }), [item, index])
  return <RepeatScopeContext.Provider value={value}>{children}</RepeatScopeContext.Provider>
}

/**
 * Read the current repeat scope. Returns `null` outside a Repeat body
 * — `$item` / `$index` expressions there resolve to `undefined`,
 * matching json-render's "no-scope is graceful" semantic.
 */
export function useRepeatScope(): RepeatScopeValue | null {
  return useContext(RepeatScopeContext)
}
